import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    CategoryClone,
    CategoryEdit,
    CustomProgressBar,
    DataList,
    Label,
    MessageBanner,
    MessageModal,
    SubCategoryClone
} from 'common/components';
import AddIndicatorMaster from 'common/components/custom/add-indicator-master/AddIndicatorMaster';
import EditIndicator from 'common/components/custom/edit-indicator/EditIndicator';
import EntityDetailsPopup from 'common/components/custom/entity-details-popup/EntityDetailsPopup';
import CategoryAddMaster from 'common/components/custom/category-add-master/CategoryAddMaster';
import { FileDetails, FieldError } from 'common/models';
import {
    ApiStatus,
    ButtonLabel,
    EntityType,
    MessageType,
    Schema,
    UploadStatus
} from 'common/enums';
import { EntityActions } from 'common/enums/actions.enum';
import {
    FILE_PREVIEW_PROGRESS,
    MAIN_ROUTES,
    MASTER_DATA_NOTIFICATION_BANNER_MESSAGE,
    MOVE_INDICATOR_TO_ANOTHER_SUBCATEGORY_DESCRIPTION,
    MOVE_INDICATOR_TO_ANOTHER_SUBCATEGORY_TITLE,
    MOVE_SUBCATEGORY_TO_ANOTHER_SUBCATEGORY_DESCRIPTION,
    MOVE_SUBCATEGORY_TO_ANOTHER_SUBCATEGORY_TITLE,
    MOVED_SUBCATEGORY_SUCCESSFULLY_MESSAGE,
    APPLY_CHANGES,
    APPLY_CHANGES_TITLE,
    LINK_CONFIRMATION_MODAL_TEXT,
    LINK_CONFIRMATION_MODAL_TITLE,
    MOVED_SUBCATEGORY_SUCCESSFULLY_TITLE,
    MOVE_INDICATOR_MODAL_TEXT,
    MOVE_INDICATOR_MODAL_TITLE,
    MOVE_SUB_CATEGORY_MODAL_TEXT,
    MOVE_SUB_CATEGORY_MODAL_TITLE,
    DATA_SYNC_IN_PROGRESS,
    ERROR_CORRECTED_MESSAGE,
    DISCARD_CHANGES_TITLE,
    DISCARD_CHANGES_DESCRIPTION
} from 'common/config';
import { CategoryDTO, EntityVM } from 'common/models/data/entity.model';
import { EntityDetailsPayload } from 'common/models/data/entityStore.model';
import { IndicatorUpdateDTO } from 'common/models/data/indicator.model';
import { EditSubCategoryDTO } from 'modules/master-data/models/master-data.model';
import { ApiInitiator } from 'common/enums/data.enum';
import { findCategoriesByHierarchyId } from 'common/utils/table';
import { fileUploadActions } from 'common/redux/file-upload';
import { RootState } from 'common/redux/core/root.reducer';
import { entityActions } from 'common/redux/entity';
import {
    getFileUploadResponseStatus,
    getSelectedFiles,
    getSyncDetails,
    getSyncDetailsStatus,
    getUploadStatus,
    getUploadingFiles
} from 'common/redux/file-upload/fileUpload.selector';
import { getMessageModalDetails } from 'common/redux/app-state/appState.selector';

import Header from 'modules/master-data/components/header/Header';
import SearchResultList from 'modules/master-data/components/search-result-list/SearchResultList';
import AddSubCategoryPopup from 'modules/master-data/components/add-sub-category-popup/AddSubCategoryPopup';
import AddAndMoveSubCategoryPopup from 'modules/master-data/components/add-and-move-sub-category-popup/AddAndMoveSubCategoryPopup';
import EntityArchive from 'modules/master-data/components/entity-archive/EntityArchive';
import EntityDelete from 'modules/master-data/components/entity-delete/EntityDelete';
import IndicatorCopy from 'modules/master-data/components/indicator-copy/IndicatorCopy';
import LinkIndicatorPopup from 'modules/master-data/components/link-indicator/LinkIndicator';
import MoveWizardPopup from 'modules/master-data/components/move-wizard-popup/MoveWizardPopup';
import MasterSubCategoryEdit from 'modules/master-data/components/master-sub-category-edit/MasterSubCategoryEdit';
import { Mode } from 'modules/data-editor/enums/mode.enum';
import { getInitialMasterListColDefs } from 'modules/master-data/config';
import { masterDataActions } from 'modules/master-data/redux';
import {
    getMode,
    getFieldErrors,
    getEditModalVisibility,
    getAddModalVisibility,
    getMessageBanner,
    getMasterDataSelector,
    getHierarchicalDataSelector,
    getReviewStatus,
    getCurrentMasterData,
    getScrollToTopFlag,
    getHierarchicalDataResponse,
    getSubcategoryExistWithSameName,
    getIsMovableSubCategoryResponse,
    getIndicatorCopyResponse
} from 'modules/master-data/redux/masterData.selector';
import EntityExport from 'modules/master-data/components/entity-export/EntityExport';
import { ExportRequestDto } from 'modules/master-data/models/export.model';
import {
    createExportRequestPayload,
    getIsApplyDiscardAccess,
    getisEditActionAccess
} from 'modules/master-data/utils/master-data.utils';
import useMasterDataAccess from 'modules/master-data/role-access-hooks/useMasterDataAccess';

import './MasterData.scss';

const MasterData: FC = () => {
    /**
     * State variable for add subcategory popup open and close
     */
    const [addSubCategoryFlag, setAddSubCategoryFlag] = useState(false);

    /**
     * State variable for add subcategory popup being open from Move popup
     */
    const [addAndMoveSubCategoryFlag, setAddAndMoveSubCategoryFlag] = useState(false);
    const [moveSubCatToAnotherAlertFlag, setMoveSubCatToAnotherAlertFlag] = useState(false);
    const [hierarchicalViewPopupFlag, setHierarchicalViewPopupFlag] = useState(false);
    const [isLinkIndicatorVisible, setIsLinkIndicatorVisible] = useState(false);
    const [isIndicatorCopyVisibile, setIsIndicatorCopyVisible] = useState(false);
    const [isShowDetailsPopup, setShowDetailsPopup] = useState<boolean>(false);
    const [sourceModalData, setSourceModalData] = useState<EntityVM>();
    const [indicatorToLink, setIndicatorToLink] = useState<EntityVM>();
    const [isCategoryCloneVisible, setIsCategoryCloneVisible] = useState<boolean>(false);
    const [isSubCategoryCloneVisible, setIsSubCategoryCloneVisible] = useState<boolean>(false);
    const [isEntityArchiveVisible, setIsEntityArchiveVisible] = useState<boolean>(false);
    const [isEntityDeleteVisible, setIsEntityDeleteVisible] = useState<boolean>(false);
    const [searchVal, setSearchVal] = useState<string>('');
    const [isShowPopup, setShowPopup] = useState<boolean>(false);
    const [addNewIndicatorFlag, setAddNewIndicatorFlag] = useState(false);
    const [popupDetails, setPopupDetails] = useState({ title: '', text: '', type: '' });
    const [targetModelData, setTargetModelData] = useState<any>(null);
    const [entityDetailsPayload, setEntityDetailsPayload] = useState<EntityDetailsPayload>();
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [hierarchyPageNumber, setHierarchyPageNumber] = useState<number>(1);
    const [createNewEntityType, setCreateNewEntityType] = useState('');
    const [isEntityExportVisible, setIsEntityExportVisible] = useState<boolean>(false);
    const [exportReqPayload, setExportReqPayload] = useState<ExportRequestDto>();

    const masterDataFeatureActionList = useMasterDataAccess();
    const isApplyDiscardDisabled = getIsApplyDiscardAccess(masterDataFeatureActionList);

    const selectedFiles = useSelector((state: RootState) => getSelectedFiles(state));
    const uploadingFiles = useSelector((state: RootState) => getUploadingFiles(state));
    const mode = useSelector((state: RootState) => getMode(state));
    const masterData = useSelector((state: RootState) => getMasterDataSelector(state));
    const fieldErrors = useSelector((state: RootState) => getFieldErrors(state));
    const isEditModalVisible = useSelector((state: RootState) => getEditModalVisibility(state));
    const messageBanner = useSelector((state: RootState) => getMessageBanner(state));
    const hierarchicalData = useSelector((state: RootState) => getHierarchicalDataSelector(state));
    const hierarchicalDataResponse = useSelector((state: RootState) =>
        getHierarchicalDataResponse(state)
    );
    const reviewStatus = useSelector((state: RootState) => getReviewStatus(state));
    const uploadResponseStatus = useSelector((state: RootState) =>
        getFileUploadResponseStatus(state)
    );
    const isShowAddCategoryPopup = useSelector((state: RootState) => getAddModalVisibility(state));
    const dataEditorUploadStatus = useSelector((state: RootState) => getUploadStatus(state));
    const messageModal = useSelector((state: RootState) => getMessageModalDetails(state));
    const fileSyncStatus = useSelector((state: RootState) => getSyncDetails(state));
    const fileSyncStatusApiStatus = useSelector((state: RootState) => getSyncDetailsStatus(state));
    const isScrollToTop = useSelector((state: RootState) => getScrollToTopFlag(state));
    const indicatorCopyResponse = useSelector((state: RootState) =>
        getIndicatorCopyResponse(state)
    );
    const isMovableSubCategoryResponse = useSelector((state: RootState) =>
        getIsMovableSubCategoryResponse(state)
    );

    /**
     * Following line is collecting response of dulplicate subcategory in target EntityType (Move operation)
     */
    const isSubcategoryExistWithSameName = useSelector((state: RootState) =>
        getSubcategoryExistWithSameName(state)
    );

    const isSyncInProgress =
        fileSyncStatus?.previewStatus === UploadStatus.InProgress ||
        mode === Mode.SyncInProgress ||
        mode === Mode.DataEditorSync;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const fetchHierarchyData = useCallback(
        (pageNo?: number) => {
            dispatch(
                masterDataActions.getHierarchicalData({
                    pageNumber: pageNo ? pageNo : 1,
                    pageSize: 20,
                    searchText: ''
                })
            );
        },
        [dispatch]
    );

    useEffect(() => {
        dispatch(masterDataActions.fetchReviewStatus());
        dispatch(fileUploadActions.fetchUploadStatus());
        dispatch(fileUploadActions.getSyncDetails());
    }, [dispatch]);

    useEffect(() => {
        if (
            fileSyncStatus?.previewStatus === UploadStatus.InProgress &&
            fileSyncStatus?.fileDetails?.targetSchema !== Schema.GBB
        ) {
            dispatch(masterDataActions.setMode(Mode.SyncInProgress));
        } else if (fileSyncStatus?.previewStatus === UploadStatus.Open) {
            dispatch(masterDataActions.setMode(Mode.Initial));
        }
    }, [dispatch, fileSyncStatus?.fileDetails?.targetSchema, fileSyncStatus?.previewStatus]);

    useEffect(() => {
        if (uploadResponseStatus === ApiStatus.SUCCESS) {
            dispatch(fileUploadActions.resetUploadResponse());
            dispatch(masterDataActions.setAddModalVisibility(false));
            setAddSubCategoryFlag(false);
            setAddNewIndicatorFlag(false);
            dispatch(masterDataActions.setMode(Mode.DataLoadedToPreview));
        }
    }, [dispatch, navigate, uploadResponseStatus]);

    useEffect(() => {
        if (mode === Mode.DataLoadedToPreview && !messageModal?.isVisible) {
            navigate(MAIN_ROUTES.DATA_EDITOR);
            dispatch(masterDataActions.resetToInitialState());
        }
    }, [dispatch, messageModal?.isVisible, mode, navigate]);

    useEffect(() => {
        if (
            masterData.status === ApiStatus.SUCCESS &&
            masterData.data?.pagination.currentPage === 1
        ) {
            setPageNumber(1);
        }
    }, [masterData.data?.pagination.currentPage, masterData.status]);

    useEffect(() => {
        if (
            hierarchicalDataResponse.status === ApiStatus.SUCCESS &&
            hierarchicalDataResponse.data?.pagination.currentPage === 1
        ) {
            setHierarchyPageNumber(1);
        }
    }, [hierarchicalDataResponse.data?.pagination.currentPage, hierarchicalDataResponse.status]);

    useEffect(() => {
        if (indicatorCopyResponse.status === ApiStatus.SUCCESS) {
            setIsIndicatorCopyVisible(false);
            setSourceModalData(undefined);
            dispatch(masterDataActions.resetIndicatorCopy());
        } else if (
            indicatorCopyResponse.status === ApiStatus.ERROR &&
            messageModal?.isVisible === false
        ) {
            dispatch(masterDataActions.resetIndicatorCopy());
            setIsIndicatorCopyVisible(true);
        }
    }, [dispatch, indicatorCopyResponse.status, messageModal?.isVisible]);

    const onSearchChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchVal(event.target.value);
        if (event.target.value.length < 3) {
            dispatch(masterDataActions.resetSearchResult());
        }
    };

    interface EntityVM2 extends EntityVM {
        isChildLink: boolean;
    }
    const handleDetailsClick = (rowData: EntityVM2) => {
        setShowDetailsPopup(true);
        setEntityDetailsPayload({
            entityType: rowData.entityType,
            id: rowData.id,
            initiator: ApiInitiator.MasterData,
            isChildLink: rowData.isChildLink ? true : false,
            isEditActionDisabled: getisEditActionAccess(
                rowData.entityType,
                masterDataFeatureActionList
            ),
            parentSubCategoryId: rowData.hierarchyId
        });
        setSourceModalData(rowData);
    };

    const handleHierarchicalDataToggle = (data: EntityVM) =>
        dispatch(masterDataActions.toggleHierarchicalDataExpansion(data));

    const onArchive = (data: EntityVM) => {
        setIsEntityArchiveVisible(true);
        setSourceModalData(data);
    };

    const onDelete = (data: EntityVM) => {
        setIsEntityDeleteVisible(true);
        setSourceModalData(data);
    };

    const onExport = (data: EntityVM) => {
        const reqPayload: ExportRequestDto = createExportRequestPayload(data);

        setExportReqPayload(reqPayload);
        setIsEntityExportVisible(true);
    };

    const showExportPopup = (data: ExportRequestDto) => {
        setExportReqPayload(data);
        setIsEntityExportVisible(true);
    };

    const popupHandler = () => {
        setShowPopup(false);
    };

    const onEditClick = (data: EntityDetailsPayload) => {
        dispatch(masterDataActions.setEditModalVisibility(true));
        setShowDetailsPopup(false);
        setEntityDetailsPayload(data);
    };

    const onAddSubCategory = (data: EntityVM) => {
        dispatch(masterDataActions.setHierarchicalData());
        setSourceModalData(data);
        updateSelectedRadioButton(data);
        setAddSubCategoryFlag(true);
    };

    // const showExportPopup = () => {
    //     setIsEntityExportVisible(true);
    // };

    /**
     * Following function will be called on move action
     * @param data containing source subcategory information
     */
    const onMoveSubCategory = (data: any) => {
        fetchHierarchyData();
        setPopupDetails({
            title: MOVE_SUB_CATEGORY_MODAL_TITLE,
            text: MOVE_SUB_CATEGORY_MODAL_TEXT,
            type: 'move'
        });
        setSourceModalData(data);

        /**
         * API call to get the Movable SubCategory Response
         * Based on response system allow to move or not
         */
        dispatch(masterDataActions.isMovableSubCategoryBegin({ id: data.id }));
    };

    /**
     * On API response checking Movable SubCategory
     * Based on response system allow to move
     */
    useEffect(() => {
        if (isMovableSubCategoryResponse.status == ApiStatus.SUCCESS) {
            if (isMovableSubCategoryResponse.data) {
                setHierarchicalViewPopupFlag(true);
            } else {
            }
        }
    }, [isMovableSubCategoryResponse]);

    /**
     * Following function will be called on move action
     * @param data containing source Indicator information
     */
    const onMoveIndicator = (data: EntityVM) => {
        fetchHierarchyData();
        setPopupDetails({
            title: MOVE_INDICATOR_MODAL_TITLE,
            text: MOVE_INDICATOR_MODAL_TEXT,
            type: 'moveIndicator'
        });
        setSourceModalData(data);
        setHierarchicalViewPopupFlag(true);
    };

    const onLinkIndicator = (data: EntityVM) => {
        fetchHierarchyData();
        const parentSubCategory = findCategoriesByHierarchyId(
            getCurrentMasterData(),
            data.hierarchyId
        );
        setSourceModalData(parentSubCategory);
        setIndicatorToLink(data);
        setIsLinkIndicatorVisible(true);
    };

    const closeLinkIndicatorPopup = () => {
        setIsLinkIndicatorVisible(false);
    };

    const updateSelectedRadioButton = (entity: EntityVM) => {
        /**
         * Following API call to check if duplicate subcategory exist in target EntityType
         * (Move operation)
         */
        dispatch(
            masterDataActions.getChildSubCategoryDataBegin({
                id: entity.id,
                entityType: entity.entityType,
                hierarchyId: entity.hierarchyId,
                sourceItemName: sourceModalData?.name
            })
        );

        /**
         * Following API call for radio button selection of target EntityType
         * (Move operation)
         */
        dispatch(
            masterDataActions.setRadioButtonSelected({
                targetId: entity.id
            })
        );
    };

    const onAddIndicator = (data: any) => {
        setSourceModalData(data);
        setAddNewIndicatorFlag(true);
    };

    useEffect(() => {
        dispatch(
            masterDataActions.getMasterData({
                pageNumber: pageNumber,
                pageSize: 20,
                searchText: ''
            })
        );
    }, [dispatch, pageNumber]);

    useEffect(() => {
        fetchHierarchyData(hierarchyPageNumber);
    }, [dispatch, fetchHierarchyData, hierarchyPageNumber]);

    useEffect(() => {
        if (mode === Mode.ErrorCorrected) {
            dispatch(
                masterDataActions.setMessageBanner({
                    isVisible: true,
                    message: ERROR_CORRECTED_MESSAGE
                })
            );
        }
    }, [dispatch, mode]);

    const handleSelectedFileRemove = (file: FileDetails) => () => {
        const updatedFiles = selectedFiles
            ? selectedFiles.filter((item: FileDetails) => file.name !== item.name)
            : [];
        dispatch(fileUploadActions.setSelectedFiles(updatedFiles));
    };

    const handleUpload = () => {
        dispatch(masterDataActions.setMode(Mode.Initial));
        dispatch(masterDataActions.setAddModalVisibility(false));
    };

    const handleMessageBannerClose = () => {
        dispatch(masterDataActions.setMessageBanner({ isVisible: false, message: '' }));
    };

    const handleExpansionToggle = (rowData: any) => {
        dispatch(masterDataActions.toggleExpansion(rowData));
    };

    const handleAddCategoryClose = () => {
        dispatch(masterDataActions.setAddModalVisibility(false));
        dispatch(fileUploadActions.setSelectedFiles([]));
        dispatch(fileUploadActions.setUploadingFiles([]));
        updateFieldErrors([]);
    };

    const onIndicatorCopy = (data: EntityVM) => {
        data = { ...data, initiator: ApiInitiator.MasterData };
        dispatch(masterDataActions.setHierarchicalData());
        setSourceModalData(data);
        data.parentHierarchyId &&
            dispatch(
                masterDataActions.setRadioButtonSelected({
                    targetId: data.parentHierarchyId,
                    field: 'hierarchyId'
                })
            );
        setIsIndicatorCopyVisible(true);
    };

    const onEntityClone = (data: any) => {
        fetchHierarchyData(1);
        if (data.entityType === EntityType.Category) {
            setIsCategoryCloneVisible(true);
        } else if (data.entityType === EntityType.SubCategory) {
            setIsSubCategoryCloneVisible(true);
        }
        setSourceModalData(data);
    };

    const handleAction = (action: EntityActions, data: EntityVM) => {
        switch (action) {
            case EntityActions.Edit: {
                onEditClick({
                    entityType: data.entityType,
                    id: data.entityType === EntityType.SubCategory ? data.hierarchyId : data.id,
                    initiator: ApiInitiator.MasterData
                });
                setSourceModalData(data);
                break;
            }
            case EntityActions.Delete: {
                onDelete(data);
                break;
            }
            case EntityActions.AddNewSubCategory: {
                onAddSubCategory(data);
                break;
            }
            case EntityActions.Move: {
                if (data.entityType == EntityType.SubCategory) {
                    onMoveSubCategory(data);
                } else if (data.entityType == EntityType.Indicator) {
                    onMoveIndicator(data);
                }
                break;
            }
            case EntityActions.AddNewIndicator: {
                onAddIndicator(data);
                break;
            }
            case EntityActions.Copy: {
                onIndicatorCopy(data);
                break;
            }
            case EntityActions.Clone: {
                onEntityClone(data);
                break;
            }
            case EntityActions.Link: {
                onLinkIndicator(data);
                break;
            }
            case EntityActions.Archive: {
                onArchive(data);
                break;
            }
            case EntityActions.Export: {
                onExport(data);
                break;
            }
            default:
                break;
        }
    };

    const handleEditClose = () => {
        dispatch(masterDataActions.setEditModalVisibility(false));
        dispatch(masterDataActions.setFieldErrors([]));
        dispatch(entityActions.resetIndicatorDetails());
    };

    const handleCategoryUpdate = (updatedData: CategoryDTO) => {
        dispatch(masterDataActions.updateCategory(updatedData));
    };

    const handleAddCategory = (addData: CategoryDTO) => {
        dispatch(masterDataActions.addCategory(addData));
    };

    const showAddCategoryPopup = () => {
        if (mode === Mode.ErrorCorrected) {
            handleMessageBannerClose();
            dispatch(masterDataActions.saveToMaster());
        } else dispatch(masterDataActions.setAddModalVisibility(true));
    };

    const applyChanges = (EntityAction: string) => {
        setPopupDetails({
            title: APPLY_CHANGES_TITLE,
            text: APPLY_CHANGES,
            type: EntityAction
        });

        setShowPopup(true);
    };

    const handleChangeDiscard = () => {
        setPopupDetails({
            title: DISCARD_CHANGES_TITLE,
            text: DISCARD_CHANGES_DESCRIPTION,
            type: EntityActions.DiscardChanges
        });
        setShowPopup(true);
    };

    const handleSubCategoryUpdate = (updatedData: EditSubCategoryDTO) => {
        dispatch(masterDataActions.updateSubCategory(updatedData));
    };

    const updateFieldErrors = (errors: Array<FieldError>) => {
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    const handleIndicatorUpdate = (updatedData: IndicatorUpdateDTO) => {
        dispatch(
            masterDataActions.updateIndicator({
                editedIndicator: updatedData,
                indicator: sourceModalData
            })
        );
        dispatch(entityActions.resetIndicatorDetails());
    };

    const saveHandler = (createNew: string, data: any) => {
        if (popupDetails.type === 'link') {
            setPopupDetails({
                title: LINK_CONFIRMATION_MODAL_TITLE,
                text: LINK_CONFIRMATION_MODAL_TEXT,
                type: 'linkConfirmation'
            });
            setTargetModelData(data);
            setShowPopup(true);
            setHierarchicalViewPopupFlag(false);
        } else if (popupDetails.type === 'move' || popupDetails.type === 'moveIndicator') {
            setCreateNewEntityType(createNew);
            setTargetModelData(data);
            setMoveSubCatToAnotherAlertFlag(true);
            setHierarchicalViewPopupFlag(false);
        }
    };

    const openPopUpToCreateNewSubCategory = () => {
        setAddAndMoveSubCategoryFlag(true);
        setHierarchicalViewPopupFlag(false);
    };

    const handleIndicatorCopyClose = (resetData = false) => {
        setIsIndicatorCopyVisible(false);
        updateFieldErrors([]);
        setHierarchyPageNumber(1);
        resetData && setSourceModalData(undefined);
        resetData && dispatch(entityActions.resetIndicatorDetails());
    };

    /**
     * Back button from create new subcategory popup
     */
    const onBackButtonClickedSubCategory = () => {
        setHierarchicalViewPopupFlag(true);
        setAddAndMoveSubCategoryFlag(false);
    };

    const handleCategoryCloneClose = () => {
        setIsCategoryCloneVisible(false);
        setHierarchyPageNumber(1);
        setSourceModalData(undefined);
    };

    const handleSubCategoryCloneClose = () => {
        setIsSubCategoryCloneVisible(false);
        setHierarchyPageNumber(1);
        setSourceModalData(undefined);
    };

    /**
     * To handle infinite scroll
     */
    const handleScroll = () => {
        if (
            masterData.status !== ApiStatus.LOADING &&
            masterData.data?.pagination &&
            masterData.data?.pagination.totalPages > pageNumber
        ) {
            setPageNumber(pageNumber + 1);
        }
    };

    const resetScrollToTop = () => {
        dispatch(masterDataActions.setScrollToTop(false));
    };

    const handleSubcategoryAddClose = () => {
        setAddSubCategoryFlag(false);
        dispatch(fileUploadActions.setSelectedFiles([]));
        dispatch(fileUploadActions.setUploadingFiles([]));
        updateFieldErrors([]);
    };

    const handleIndicatorAddClose = () => {
        setAddNewIndicatorFlag(false);
        dispatch(fileUploadActions.setSelectedFiles([]));
        dispatch(fileUploadActions.setUploadingFiles([]));
        updateFieldErrors([]);
    };

    const handleArchiveClose = () => {
        setIsEntityArchiveVisible(false);
        setHierarchyPageNumber(1);
        setSourceModalData(undefined);
    };

    const handleDeleteClose = () => {
        setIsEntityDeleteVisible(false);
        setHierarchyPageNumber(1);
        setSourceModalData(undefined);
    };

    const isUploadDisabled =
        dataEditorUploadStatus?.data?.previewStatus === UploadStatus.InProgress ||
        dataEditorUploadStatus?.data?.previewStatus === UploadStatus.Validation;

    const uploadedUser = dataEditorUploadStatus?.data?.fileDetails?.uploadedUser?.displayName;

    /**
     * Move Subcategory API payload prepration and API call
     */
    const prepareMovSubCategoryPayloadAndCallAPI = () => {
        let movementOption = 4;
        if (
            targetModelData?.targetModelData?.categoryId == sourceModalData?.catId &&
            targetModelData?.targetModelData?.entityType == EntityType.Category
        ) {
            movementOption = 1;
        } else if (
            targetModelData?.targetModelData?.categoryId == sourceModalData?.catId &&
            targetModelData?.targetModelData?.entityType == EntityType.SubCategory
        ) {
            movementOption = 2;
        } else if (
            targetModelData?.targetModelData?.categoryId !== sourceModalData?.catId &&
            targetModelData?.targetModelData?.entityType == EntityType.Category &&
            createNewEntityType == ''
        ) {
            movementOption = 3;
        }

        dispatch(
            masterDataActions.moveSubCategoryBegin({
                inputData:
                    createNewEntityType == ''
                        ? {
                              targetCategoryId: targetModelData.targetModelData?.categoryId,
                              targetSubCategoryId:
                                  targetModelData.targetModelData?.entityType ==
                                  EntityType.SubCategory
                                      ? targetModelData.targetModelData.id
                                      : null,
                              targetHierarchyId: targetModelData.targetModelData.hierarchyId,
                              sourceHierarchyId: sourceModalData?.hierarchyId,
                              targetParentHierarchyId:
                                  targetModelData.targetModelData.parentHierarchyId,
                              targetSubCategoryLevel: targetModelData.targetModelData
                                  .subCategoryLevel
                                  ? parseInt(targetModelData.targetModelData.subCategoryLevel) + 1
                                  : 1,
                              sourceSubCategoryId: sourceModalData?.id
                          }
                        : createNewEntityType == 'createNewCategory'
                        ? {
                              targetCategoryId: null,
                              targetSubCategoryId: null,
                              targetHierarchyId: null,
                              sourceHierarchyId: sourceModalData?.hierarchyId,
                              targetParentHierarchyId: null,
                              targetSubCategoryLevel: 0,
                              sourceSubCategoryId: sourceModalData?.id,
                              addSubCategoryDto: {
                                  subCategoryName: targetModelData.data.subCatName,
                                  order: targetModelData.data.subCatOrder,
                                  description: targetModelData.data.subCatDescription,
                                  parentSubCategoryId: null,
                                  parentHierarchyId: null,
                                  categoryId: null,
                                  subCategoryLevel: 1
                              },
                              addCategoryDto: {
                                  categoryName: targetModelData.data.catName,
                                  categoryOrder: targetModelData.data.catOrder,
                                  description: targetModelData.data.catDescription
                              }
                          }
                        : {
                              targetCategoryId: targetModelData.targetModelData?.categoryId,
                              targetSubCategoryId:
                                  targetModelData.targetModelData?.entityType ==
                                  EntityType.SubCategory
                                      ? targetModelData.targetModelData.id
                                      : null,
                              targetHierarchyId: targetModelData.targetModelData.hierarchyId,
                              sourceHierarchyId: sourceModalData?.hierarchyId,
                              targetParentHierarchyId:
                                  targetModelData.targetModelData.parentHierarchyId,
                              targetSubCategoryLevel: targetModelData.targetModelData
                                  .subCategoryLevel
                                  ? parseInt(targetModelData.targetModelData.subCategoryLevel) + 1
                                  : 1,
                              sourceSubCategoryId: sourceModalData?.id,
                              addSubCategoryDto: {
                                  subCategoryName: targetModelData.data.subCatName,
                                  order: targetModelData.data.subCatOrder,
                                  description: targetModelData.data.subCatDescription,
                                  parentSubCategoryId:
                                      targetModelData.targetModelData.parentSubCategoryId,
                                  parentHierarchyId:
                                      targetModelData.targetModelData.parentHierarchyId,
                                  categoryId: targetModelData.targetModelData?.categoryId,
                                  subCategoryLevel: targetModelData.targetModelData.subCategoryLevel
                                      ? parseInt(targetModelData.targetModelData.subCategoryLevel) +
                                        1
                                      : 1
                              }
                          },
                createNewEntityType: createNewEntityType,
                movementOption: movementOption
            })
        );
        setCreateNewEntityType('');
    };

    /**
     * Method to handle the confirmation popup
     */
    const handleConfirmationPopup = () => {
        if (popupDetails.type == 'move_success') {
            navigate('/variants/master-data');
            setShowPopup(false);
        }
        if (popupDetails.type == EntityActions.Apply) {
            dispatch(
                masterDataActions.applyChanges({
                    sourceSchema: Schema.MasterReview,
                    targetSchema: Schema.Master
                })
            );
        }
        if (popupDetails.type == EntityActions.GBBPublic) {
            dispatch(
                masterDataActions.applyChanges({
                    sourceSchema: Schema.Master,
                    targetSchema: Schema.GBB
                })
            );
        }
        if (popupDetails.type == EntityActions.DiscardChanges) {
            dispatch(masterDataActions.discardChanges());
        }
        popupHandler();
    };

    /**
     * Move indicator API payload prepration and API call
     */
    const prepareMoveIndicatorAndCallAPI = () => {
        dispatch(
            masterDataActions.moveIndicatorBegin({
                inputData:
                    createNewEntityType == ''
                        ? {
                              targetHierarchyId: targetModelData?.targetModelData.hierarchyId,
                              sourceHierarchyId: sourceModalData?.hierarchyId,
                              indicatorId: sourceModalData?.id
                          }
                        : createNewEntityType == 'createNewCategory'
                        ? {
                              targetHierarchyId: null,
                              sourceHierarchyId: sourceModalData?.hierarchyId,
                              indicatorId: sourceModalData?.id,
                              addSubCategoryDto: {
                                  subCategoryName: targetModelData.data.subCatName,
                                  order: targetModelData.data.subCatOrder,
                                  description: targetModelData.data.subCatDescription,
                                  parentSubCategoryId: null,
                                  parentHierarchyId: null,
                                  categoryId: null,
                                  subCategoryLevel: 1
                              },
                              addCategoryDto: {
                                  categoryName: targetModelData.data.catName,
                                  categoryOrder: targetModelData.data.catOrder,
                                  description: targetModelData.data.catDescription
                              }
                          }
                        : {
                              targetHierarchyId: targetModelData?.targetModelData.hierarchyId,
                              sourceHierarchyId: sourceModalData?.hierarchyId,
                              indicatorId: sourceModalData?.id,
                              addSubCategoryDto: {
                                  subCategoryName: targetModelData.data.subCatName,
                                  order: targetModelData.data.subCatOrder,
                                  description: targetModelData.data.subCatDescription,
                                  parentSubCategoryId:
                                      targetModelData.targetModelData.entityType ==
                                      EntityType.SubCategory
                                          ? targetModelData.targetModelData.id
                                          : null,
                                  parentHierarchyId: targetModelData.targetModelData.hierarchyId,
                                  categoryId: targetModelData.targetModelData.catId,
                                  subCategoryLevel: targetModelData.targetModelData.subCategoryLevel
                                      ? parseInt(targetModelData.targetModelData.subCategoryLevel) +
                                        1
                                      : 1
                              }
                          },
                createNewEntityType: createNewEntityType
            })
        );
        setCreateNewEntityType('');
    };

    const fetchNextHierachySet = () => {
        if (
            hierarchicalDataResponse.status !== ApiStatus.LOADING &&
            hierarchicalDataResponse.data?.pagination &&
            hierarchicalDataResponse.data?.pagination.totalPages > hierarchyPageNumber
        ) {
            setHierarchyPageNumber(hierarchyPageNumber + 1);
        }
    };

    return (
        <div className="master-data-container">
            <Header
                reviewStatus={reviewStatus}
                searchVal={searchVal}
                dataEditorMode={mode}
                showAddCategoryPopup={showAddCategoryPopup}
                showExportPopup={showExportPopup}
                isSyncInProgress={isSyncInProgress}
                isExportActionDisabled={
                    masterDataFeatureActionList.masterDataFeatureAccess.isExportMasterDataDisabled
                }
                isAddNewCategoryButtonDisabled={
                    masterDataFeatureActionList.categoryFeatureAccess.isAddNewCategoryDisabled
                }
                isMapToGBBPublicDisabled={
                    masterDataFeatureActionList.masterDataFeatureAccess.isMappingToGBBPublicDisabled
                }
                applyChanges={applyChanges}
                onSearchChangeHandler={onSearchChangeHandler}
                onChangeDiscard={handleChangeDiscard}
                isApplyDiscardDisabled={isApplyDiscardDisabled}
            />
            {messageBanner.isVisible ? (
                <div className="data-editor-message-banner">
                    <MessageBanner
                        message={messageBanner.message}
                        onClose={handleMessageBannerClose}
                    />
                </div>
            ) : searchVal.length < 3 ? (
                <Label
                    className="data-editor-message-banner"
                    text={MASTER_DATA_NOTIFICATION_BANNER_MESSAGE}></Label>
            ) : (
                ''
            )}

            <div className="master-data-list-container">
                {searchVal.length >= 3 ? (
                    <SearchResultList
                        isUploadDisabled={isUploadDisabled}
                        uploadedUser={uploadedUser ?? ''}
                        handleAction={handleAction}
                        handleDetailsClick={handleDetailsClick}
                        searchVal={searchVal}
                        isSyncInProgress={isSyncInProgress}
                        masterDataFeatureActionList={masterDataFeatureActionList}
                    />
                ) : (
                    <DataList
                        isNoDataVisible={
                            fileSyncStatusApiStatus !== ApiStatus.LOADING &&
                            mode !== Mode.SyncInProgress &&
                            masterData?.data?.data?.length === 0
                        }
                        data={mode === Mode.SyncInProgress ? [] : masterData.data?.data || []}
                        fetchNextDataSet={handleScroll}
                        isScrollToTop={isScrollToTop}
                        resetScrollToTop={resetScrollToTop}
                        columnDefs={getInitialMasterListColDefs(
                            handleExpansionToggle,
                            handleDetailsClick,
                            handleAction,
                            isUploadDisabled,
                            uploadedUser ?? '',
                            isSyncInProgress,
                            masterDataFeatureActionList
                        )}
                    />
                )}
            </div>
            {mode === Mode.PreviewGeneration && (
                <CustomProgressBar isIndeterminate labelComponent={FILE_PREVIEW_PROGRESS} />
            )}
            {mode === Mode.SyncInProgress && fileSyncStatusApiStatus !== ApiStatus.LOADING && (
                <CustomProgressBar isIndeterminate labelComponent={DATA_SYNC_IN_PROGRESS} />
            )}
            {entityDetailsPayload && isShowDetailsPopup && (
                <EntityDetailsPopup
                    closeHandler={() => setShowDetailsPopup(false)}
                    isShowDetailsPopup={isShowDetailsPopup}
                    handleEditClick={onEditClick}
                    entity={entityDetailsPayload}
                />
            )}
            {entityDetailsPayload?.entityType === EntityType.Indicator && isEditModalVisible && (
                <EditIndicator
                    data={entityDetailsPayload}
                    fieldErrors={fieldErrors}
                    onClose={handleEditClose}
                    onUpdate={handleIndicatorUpdate}
                    updateFieldErrors={updateFieldErrors}
                    isEditModalVisible={isEditModalVisible}
                />
            )}
            {sourceModalData &&
                sourceModalData.entityType === EntityType.SubCategory &&
                isEditModalVisible && (
                    <MasterSubCategoryEdit
                        parentCategoryId={sourceModalData.catId}
                        parentSubCategoryId={
                            sourceModalData.parentSubCategoryId
                                ? sourceModalData.parentSubCategoryId
                                : ''
                        }
                        parentHierarchyId={
                            sourceModalData.parentHierarchyId
                                ? sourceModalData.parentHierarchyId
                                : ''
                        }
                        subCategoryLevel={sourceModalData.level}
                        isEditModalVisible={isEditModalVisible}
                        subCategoryId={sourceModalData.id ? sourceModalData.id : ''}
                        hierarchyId={sourceModalData.hierarchyId ? sourceModalData.hierarchyId : ''}
                        fieldErrors={fieldErrors}
                        onClose={handleEditClose}
                        onUpdate={handleSubCategoryUpdate}
                        updateFieldErrors={updateFieldErrors}
                    />
                )}
            {entityDetailsPayload?.entityType === EntityType.Category && isEditModalVisible && (
                <CategoryEdit
                    isEditModalVisible={isEditModalVisible}
                    data={entityDetailsPayload}
                    fieldErrors={fieldErrors}
                    onClose={handleEditClose}
                    onUpdate={handleCategoryUpdate}
                    updateFieldErrors={updateFieldErrors}
                />
            )}
            {isShowPopup && (
                <MessageModal
                    headerName={popupDetails.title}
                    message={popupDetails.text}
                    isVisible={isShowPopup}
                    isSecondaryButtonVisible={popupDetails.type == 'move_success' ? false : true}
                    type={popupDetails.type == 'move_success' ? MessageType.Success : -1}
                    primaryButton={ButtonLabel.Ok}
                    secondaryButton={ButtonLabel.Cancel}
                    onPrimaryAction={handleConfirmationPopup}
                    onSecondaryAction={() => {
                        setShowPopup(false);
                    }}
                />
            )}
            {isShowAddCategoryPopup && (
                <CategoryAddMaster
                    fieldErrors={fieldErrors}
                    isVisible={isShowAddCategoryPopup}
                    selectedFiles={selectedFiles ? selectedFiles : []}
                    uploadingFiles={uploadingFiles}
                    isUploadDisabled={isUploadDisabled}
                    uploadedUser={uploadedUser ?? ''}
                    onClose={handleAddCategoryClose}
                    onAdd={handleAddCategory}
                    updateFieldErrors={updateFieldErrors}
                    onSelectedFileRemove={handleSelectedFileRemove}
                />
            )}
            {/* Model popup for adding sub category */}
            {sourceModalData && addSubCategoryFlag && (
                <AddSubCategoryPopup
                    parentEntity={sourceModalData}
                    fieldErrors={fieldErrors}
                    selectedFiles={selectedFiles ? selectedFiles : []}
                    uploadingFiles={uploadingFiles}
                    isUploadDisabled={isUploadDisabled}
                    uploadedUser={uploadedUser ?? ''}
                    hierarchicalData={hierarchicalData}
                    isVisible={addSubCategoryFlag}
                    updateFieldErrors={updateFieldErrors}
                    updateSelectedRadioButton={updateSelectedRadioButton}
                    closePopupHandler={handleSubcategoryAddClose}
                    onUpload={handleUpload}
                    toggleExpansion={handleHierarchicalDataToggle}
                    fetchNextHierachySet={fetchNextHierachySet}
                />
            )}
            {/* Model popup for moving sub category */}
            {sourceModalData && (
                <AddAndMoveSubCategoryPopup
                    entityId={sourceModalData.id}
                    fieldErrors={fieldErrors}
                    updateFieldErrors={updateFieldErrors}
                    entityType={sourceModalData.entityType}
                    isVisible={addAndMoveSubCategoryFlag}
                    updateSelectedRadioButton={updateSelectedRadioButton}
                    onBackButtonClicked={onBackButtonClickedSubCategory}
                    closePopupHandler={() => setAddAndMoveSubCategoryFlag(false)}
                    hierarchicalData={hierarchicalData}
                    toggleExpansion={handleHierarchicalDataToggle}
                    saveHandler={saveHandler}
                    fetchNextHierachySet={fetchNextHierachySet}
                />
            )}
            {sourceModalData && hierarchicalViewPopupFlag && (
                <MoveWizardPopup
                    entityId={sourceModalData.id}
                    entityType={sourceModalData.entityType}
                    isVisible={hierarchicalViewPopupFlag}
                    updateSelectedRadioButton={updateSelectedRadioButton}
                    closePopupHandler={() => setHierarchicalViewPopupFlag(false)}
                    modalDetails={popupDetails}
                    saveHandler={saveHandler}
                    updateFieldErrors={updateFieldErrors}
                    fieldErrors={fieldErrors}
                    hierarchicalData={hierarchicalData}
                    toggleExpansion={handleHierarchicalDataToggle}
                    openPopUpToCreateNewSubCategory={openPopUpToCreateNewSubCategory}
                    setCreateNewEntityType={setCreateNewEntityType}
                    fetchNextHierachySet={fetchNextHierachySet}
                    primaryButtonDisable={isSubcategoryExistWithSameName}
                />
            )}
            {indicatorToLink && isLinkIndicatorVisible && (
                <LinkIndicatorPopup
                    isVisible={isLinkIndicatorVisible}
                    indicatorToLink={indicatorToLink}
                    updateSelectedRadioButton={updateSelectedRadioButton}
                    closePopupHandler={() => closeLinkIndicatorPopup()}
                    hierarchicalData={hierarchicalData}
                    toggleExpansion={handleHierarchicalDataToggle}
                    parentSubCategory={sourceModalData}
                    fetchNextHierachySet={fetchNextHierachySet}
                />
            )}
            {addNewIndicatorFlag && sourceModalData && (
                <AddIndicatorMaster
                    isVisible={addNewIndicatorFlag}
                    subCategoryId={sourceModalData.hierarchyId}
                    onUpload={handleUpload}
                    selectedFiles={selectedFiles ? selectedFiles : []}
                    uploadingFiles={uploadingFiles}
                    onSelectedFileRemove={handleSelectedFileRemove}
                    onClose={handleIndicatorAddClose}
                />
            )}
            {sourceModalData && (
                <IndicatorCopy
                    data={sourceModalData}
                    isVisible={isIndicatorCopyVisibile}
                    hierarchicalData={hierarchicalData}
                    primaryButtonDisable={isSubcategoryExistWithSameName}
                    fetchNextHierachySet={fetchNextHierachySet}
                    onClose={handleIndicatorCopyClose}
                />
            )}
            {moveSubCatToAnotherAlertFlag && (
                /**
                 * Follwoing model popup is confirmation popup before moving sub category, one to another
                 */
                <MessageModal
                    headerName={
                        sourceModalData?.entityType == EntityType.SubCategory &&
                        popupDetails.type === 'move'
                            ? MOVE_SUBCATEGORY_TO_ANOTHER_SUBCATEGORY_TITLE
                            : MOVE_INDICATOR_TO_ANOTHER_SUBCATEGORY_TITLE
                    }
                    message={
                        sourceModalData?.entityType == EntityType.SubCategory &&
                        popupDetails.type === 'move'
                            ? MOVE_SUBCATEGORY_TO_ANOTHER_SUBCATEGORY_DESCRIPTION
                            : MOVE_INDICATOR_TO_ANOTHER_SUBCATEGORY_DESCRIPTION
                    }
                    isVisible={moveSubCatToAnotherAlertFlag}
                    isSecondaryButtonVisible={true}
                    primaryButton={ButtonLabel.Ok}
                    secondaryButton={ButtonLabel.Cancel}
                    onPrimaryAction={() => {
                        setPopupDetails({
                            title: MOVED_SUBCATEGORY_SUCCESSFULLY_TITLE,
                            text: MOVED_SUBCATEGORY_SUCCESSFULLY_MESSAGE,
                            type: 'move_success'
                        });
                        setHierarchicalViewPopupFlag(false);
                        setMoveSubCatToAnotherAlertFlag(false);
                        if (
                            sourceModalData?.entityType == EntityType.SubCategory ||
                            sourceModalData?.entityType == EntityType.Category
                        ) {
                            if (popupDetails.type === 'move') {
                                prepareMovSubCategoryPayloadAndCallAPI();
                            } else {
                                dispatch(
                                    masterDataActions.addSubCategory({
                                        parentHierarchyId: sourceModalData.parentHierarchyId,
                                        categoryId: targetModelData.id,
                                        subCategoryName: sourceModalData.name,
                                        order: Number(sourceModalData.order),
                                        description: sourceModalData.description
                                            ? sourceModalData.description
                                            : '',
                                        parentSubCategoryId: '',
                                        subCategoryLevel: sourceModalData.level
                                    })
                                );
                            }
                        } else {
                            if (popupDetails.type === 'moveIndicator') {
                                prepareMoveIndicatorAndCallAPI();
                            } else {
                                if (sourceModalData)
                                    dispatch(
                                        masterDataActions.addIndicatorSuccess({
                                            id: targetModelData.id,
                                            name: sourceModalData.name,
                                            order: sourceModalData.order,
                                            entityType: sourceModalData.entityType, //EntityType.SubCategory,
                                            hasChild: false,
                                            hasIndicator: false
                                        })
                                    );
                            }
                        }
                        setHierarchicalViewPopupFlag(false);
                    }}
                    onSecondaryAction={() => {
                        setMoveSubCatToAnotherAlertFlag(false);
                    }}
                />
            )}
            {isCategoryCloneVisible && sourceModalData && (
                <CategoryClone data={sourceModalData} onClose={handleCategoryCloneClose} />
            )}
            {isSubCategoryCloneVisible && sourceModalData && (
                <SubCategoryClone
                    data={sourceModalData}
                    onClose={handleSubCategoryCloneClose}
                    fetchNextHierachySet={fetchNextHierachySet}
                />
            )}
            {isEntityArchiveVisible && sourceModalData && (
                <EntityArchive data={sourceModalData} onClose={handleArchiveClose} />
            )}
            {isEntityDeleteVisible && sourceModalData && (
                <EntityDelete data={sourceModalData} onClose={handleDeleteClose} />
            )}
            {isEntityExportVisible && (
                <EntityExport
                    data={exportReqPayload}
                    onClose={setIsEntityExportVisible}
                    isConfirmationVisible={isEntityExportVisible}
                    setIsConfirmationVisible={setIsEntityExportVisible}
                />
            )}
        </div>
    );
};

export default MasterData;
